import { type ReperformanceStep } from "../../../../models/audit/attributeTestColumn";
import { type Citation } from "../../../../models/audit/citation";

/** One displayed citation: a live source doc plus its optional rendered
 * screenshot, collapsed by shared `encodedCitationString` (the screenshot is the
 * preferred view target). */
export interface CitationItem {
  source: Citation;
  screenshot: Citation | null;
}

/** A reperformance step and the citations it relied on, for the grouped list. */
export interface AgentStepGroup {
  key: string;
  stepIndex: number;
  stepName: string;
  items: CitationItem[];
}

/**
 * Group reperformance citations under their step. Within a step, a
 * source/screenshot (e.g. xlsx + its image) pair collapses to one item by shared
 * `encodedCitationString`; nothing is deduped across steps, so evidence the agent
 * reused in several steps appears under each step that used it. Steps with no
 * citations are dropped. Drives both the Agent Steps list and its keyboard nav.
 */
export function groupAgentCitationsByStep(
  reperformanceSteps: ReperformanceStep[],
  isScreenshotDoc: (documentId: string | null | undefined) => boolean,
): AgentStepGroup[] {
  return reperformanceSteps
    .map((step, stepIndex) => {
      // A screenshot shares its source's `encodedCitationString`; collapse that
      // pair to one item (preferring the screenshot for viewing). Everything else
      // stays its own item — including two DISTINCT citations that merely share an
      // encoded string (e.g. two annotations on the same PDF element), which must
      // not be merged or one would silently disappear from the list.
      const sourceKeys = new Set(
        step.citations
          .filter((c) => !isScreenshotDoc(c.documentId))
          .map((c) => c.encodedCitationString || c.id || ""),
      );
      const screenshotByKey = new Map<string, Citation>();
      for (const citation of step.citations) {
        if (!isScreenshotDoc(citation.documentId)) continue;
        const key = citation.encodedCitationString || citation.id || "";
        if (!screenshotByKey.has(key)) screenshotByKey.set(key, citation);
      }
      const items: CitationItem[] = [];
      for (const citation of step.citations) {
        const key = citation.encodedCitationString || citation.id || "";
        if (isScreenshotDoc(citation.documentId)) {
          // Skip a screenshot that has a source sibling (surfaced via that
          // source); show orphan screenshots on their own.
          if (sourceKeys.has(key)) continue;
          items.push({ source: citation, screenshot: null });
        } else {
          items.push({
            source: citation,
            screenshot: screenshotByKey.get(key) ?? null,
          });
        }
      }
      return {
        key: step.id ?? `step-${stepIndex}`,
        stepIndex,
        stepName: step.stepName,
        items,
      };
    })
    .filter((group) => group.items.length > 0);
}
